/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useContext, useState } from 'react';
import { Form, FormGroup, Input, InputGroup, InputGroupAddon, Label, Button, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody } from 'reactstrap';
import DateTimePicker from 'react-datetime-picker';
import moment from 'moment';
import Alert from 'react-s-alert';

// style imports
import './Refunds.css'

// context imports
import { ThemeContext } from '../../contexts/ThemeContext'

// import services
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';

const Refunds = props => {

    // variables
    let timeout = 0;

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [refunds, setRefunds] = useState([]);
    const [modal, setModal] = useState({ isVisible: false, data: { index: -1, sessionId: '', dateTime: new Date(), transactionId: '', amount: '', gvAmount: '' } });
    const [pagination, setPagination] = useState({ limit: 10, page: 1 });
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [status, setStatus] = useState('');

    // contexts
    const { theme } = useContext(ThemeContext);

    // use Effects
    useEffect(() => {
        handleSearchSubmit(null);
    }, [pagination.page, pagination.limit]);

    // handle functions
    const handlePagination = (e, index) => {
        e.preventDefault();
        try {
            setPagination({ ...pagination, page: index });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Refunds handlePagination', 'Error in try catch block', err.stack);
        }
    }

    const handleSearchSubmit = e => {
        if (e) e.preventDefault();
        if (!isLoading) {
            setIsLoading(true);
            AdminService.getRefunds(pagination.limit, pagination.page, fromDate, toDate, status).then(data => {
                try {
                    setRefunds(data.refunds);
                    if (data.pagination.totalPages <= data.pagination.page) {
                        data.pagination.page = data.pagination.totalPages;
                    }
                    setPagination(data.pagination);
                    setIsLoading(false);
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Admin Refunds handleSearchSubmit', 'Error in try catch block', err.stack);
                    setIsLoading(false);
                }
            });
        }
    }

    const handleChange = e => {
        try {
            setModal({ ...modal, data: { ...modal.data, [e.target.name]: e.target.value } });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Refunds handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleDateTimeChange = value => {
        try {
            setModal({ ...modal, data: { ...modal.data, dateTime: value } });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Refunds handleDateTimeChange', 'Error in try catch block', err.stack);
        }
    }

    const handleRefundStatus = e => {
        e.preventDefault();
        try {
            setIsLoading2(true);
            AdminService.setRefund(modal.data).then(data => {
                const index = modal.data.index;
                const temp = [...refunds];
                temp[index].status = true;
                temp[index].processedOn = modal.data.dateTime;
                temp[index].transactionId = modal.data.transactionId;
                setRefunds(temp);
                setModal({ isVisible: false, data: { index: -1, sessionId: '', dateTime: '', transactionId: '', amount: '', gvAmount: '' } });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading2(false);
            GeneralService.logError('at Admin Refunds handleRefundStatus', 'Error in try catch block', err.stack);
        }
    }

    return (
        <main>
            <div className="container-fluid">
                <h3 className="my-4">Refunds</h3>
                <div className="card mb-4">
                    <div className="card-header d-flex">
                        <label className="my-auto">Refund List</label>
                    </div>
                    <div className="card-body py-0">
                        <div className="table-responsive">
                            <table className="table table-bordered mb-0" id="dataTable" width="100%" cellSpacing="0">
                                <thead>
                                    <tr>
                                        <td colSpan="5" className="border-0 px-0">
                                            <Form inline onSubmit={handleSearchSubmit}>
                                                <FormGroup className="mr-2">
                                                    <Label for="fromDate" style={theme.formBodyText} className="mr-1" >From</Label>
                                                    <Input type="date" id="fromDate" name="fromDate" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-control" style={theme.formInput} />
                                                </FormGroup>
                                                <FormGroup className="mr-2">
                                                    <Label for="toDate" style={theme.formBodyText} className="mr-1" >To</Label>
                                                    <Input type="date" id="toDate" name="toDate" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-control" style={theme.formInput} />
                                                </FormGroup>
                                                <FormGroup className="mr-2">
                                                    <Label for="status" style={theme.formBodyText} className="mr-1" >Status</Label>
                                                    <Input type="select" id="status" name="status" value={status} onChange={(e) => setStatus(e.target.value)} className="form-control" style={theme.formInput}>
                                                        <option value=''>All</option>
                                                        <option value={true}>Processed</option>
                                                        <option value={false}>Pending</option>
                                                    </Input>
                                                </FormGroup>
                                                <Button type="submit" className="my-auto" style={Object.assign({ height: 'fit-content' }, theme.formButton)} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Search</Button>
                                                <Button type="button" className="ml-auto my-auto" style={Object.assign({ height: 'fit-content' }, theme.formButton)} onClick={handleSearchSubmit} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Refresh</Button>
                                            </Form>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Session Id</th>
                                        <th>Transaction Id</th>
                                        <th>Processed On</th>
                                        <th>Amount</th>
                                        <th align='center'>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        refunds.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Refunds Table${index}`}>
                                                <tr key={index}>
                                                    <td>{item.session}</td>
                                                    <td>{item.transactionId}</td>
                                                    <td>{item.processedOn ? moment(item.processedOn).format('DD-MMM-YYYY [at] hh:mm A') : null}</td>
                                                    <td>{item.gvAmount > 0 ? `₹${item.amount} + ₹${item.gvAmount}GV` : `₹${item.amount}`}</td>
                                                    <td align='center'>{item.status ? "Done" : <Button style={theme.formButton} onClick={() => setModal({ isVisible: true, data: { ...modal.data, index: index, amount: item.amount, gvAmount: item.gvAmount, sessionId: item.session } })}>Mark Processed</Button>}</td>
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="5" className="border-0 pb-0">
                                            <div className="d-flex">
                                                <ErrorBoundary errorAt="At Admin Refunds Pagination">
                                                    <div className="p-2">Showing {pagination.totalResults == 0 ? 0 : (1 + (10 * (pagination.page - 1)))} to {(pagination.limit + (10 * (pagination.page - 1))) > pagination.totalResults ? pagination.totalResults : (pagination.limit + (10 * (pagination.page - 1)))} of {pagination.totalResults} entries</div>
                                                    <Pagination aria-label="Page navigation example" className="ml-auto">
                                                        <PaginationItem disabled={pagination.page == 1}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page - 1)}
                                                                previous
                                                                href="#" />
                                                        </PaginationItem>
                                                        {[...Array(pagination.totalPages)].map((page, i) =>
                                                            <PaginationItem active={i + 1 === pagination.page} key={i}>
                                                                <PaginationLink onClick={e => handlePagination(e, i + 1)} href="#">
                                                                    {i + 1}
                                                                </PaginationLink>
                                                            </PaginationItem>
                                                        )}
                                                        <PaginationItem disabled={pagination.page == pagination.totalPages}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page + 1)}
                                                                next
                                                                href="#" />
                                                        </PaginationItem>
                                                    </Pagination>
                                                </ErrorBoundary>
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <ErrorBoundary errorAt="At Admin Refunds Modal">
                <Modal size="lg" isOpen={modal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setModal({ isVisible: false, data: { sessionId: '', dateTime: '', transactionId: '', amount: '', gvAmount: '' } })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Data</span></ModalHeader>
                    <ModalBody>
                        <Form onSubmit={handleRefundStatus}>
                            <FormGroup>
                                <Label for="sessionId" style={theme.formBodyText} >Session Id</Label>
                                <Input disabled={true} type="text" name="sessionId" value={modal.data.sessionId} onChange={handleChange} className="form-control" style={theme.formInput} required />
                            </FormGroup>

                            <FormGroup>
                                <Label for="dateTime" style={theme.formBodyText} >Date Time</Label>
                                <DateTimePicker name="dateTime" format="dd-MM-y hh:mm a" yearPlaceholder="yyyy" monthPlaceholder="mm" dayPlaceholder="dd" hourPlaceholder="hh" minutePlaceholder="mm" onChange={handleDateTimeChange} value={modal.data.dateTime} className="form-control" style={theme.formInput} required={true} />
                            </FormGroup>

                            <FormGroup>
                                <Label for="transactionId" style={theme.formBodyText} >Transaction Id</Label>
                                <Input type="text" name="transactionId" value={modal.data.transactionId} onChange={handleChange} className="form-control" style={theme.formInput} required />
                            </FormGroup>

                            <FormGroup>
                                <Label for="gvAmount" style={theme.formBodyText} >Gift Voucher Amount</Label>
                                <Input type="number" name="gvAmount" value={modal.data.gvAmount} onChange={handleChange} className="form-control" style={theme.formInput} required />
                            </FormGroup>

                            <FormGroup>
                                <Label for="amount" style={theme.formBodyText} >Amount</Label>
                                <Input type="number" name="amount" value={modal.data.amount} onChange={handleChange} className="form-control" style={theme.formInput} required />
                            </FormGroup>

                            <Button type="submit" style={theme.formButton} disabled={isLoading2}>{isLoading2 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Submit</Button>
                        </Form>
                    </ModalBody>
                </Modal>
            </ErrorBoundary>
        </main>
    )

}

export default Refunds;